// Listing Builder'in liste ve editor tarafinda ORTAK yardimcilar: Studio'dan
// gelen aktarim, taslak liste ogesi, durum etiketleri ve puan.

export const API_BASE = import.meta.env.VITE_API_URL || "";

export interface StudioExportData {
  images: { id: string; url: string }[];
  draftId?: string;
  videoUrl?: string;
  listingTemplateId?: string;
  designId?: string;
  designSku?: string;
  mockupTemplates?: string[];
  title?: string;
  description?: string;
  tags?: string[];
  designAnalysis?: {
    analysis: string;
    keywords: string[];
    targetAudience: string;
    productTypes: string[];
    colors: string[];
  };
}
export function getStudioExports(): StudioExportData {
  // 1. Check window object first (SPA in-memory transfer - handles large data URLs)
  const win = window as unknown as Record<string, unknown>;
  if (Array.isArray(win.__studioExportImages) && win.__studioExportImages.length > 0) {
    const images = win.__studioExportImages as { id: string; url: string }[];
    const draftId = win.__studioExportDraftId as string | undefined;
    const videoUrl = win.__studioExportVideoUrl as string | undefined;
    const designAnalysis = win.__studioExportAnalysis as StudioExportData["designAnalysis"] | undefined;
    const listingTemplateId = win.__studioExportListingTemplateId as string | undefined;
    const designId = win.__studioExportDesignId as string | undefined;
    const designSku = win.__studioExportDesignSku as string | undefined;
    const mockupTemplates = win.__studioExportMockupTemplates as string[] | undefined;
    const title = win.__studioExportTitle as string | undefined;
    const description = win.__studioExportDescription as string | undefined;
    const tags = win.__studioExportTags as string[] | undefined;
    return { images, draftId, videoUrl, designAnalysis, listingTemplateId, designId, designSku, mockupTemplates, title, description, tags };
  }
  // 2. Fallback: check localStorage (legacy / small payloads)
  try {
    const raw = localStorage.getItem("erc_studio_exports");
    if (!raw) return { images: [] };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { images: parsed };
    return parsed as StudioExportData;
  } catch { return { images: [] }; }
}
export function clearStudioExports() {
  // Clear both sources
  const win = window as unknown as Record<string, unknown>;
  delete win.__studioExportImages;
  delete win.__studioExportDraftId;
  delete win.__studioExportVideoUrl;
  delete win.__studioExportAnalysis;
  delete win.__studioExportListingTemplateId;
  delete win.__studioExportDesignId;
  delete win.__studioExportDesignSku;
  delete win.__studioExportMockupTemplates;
  delete win.__studioExportTitle;
  delete win.__studioExportDescription;
  delete win.__studioExportTags;
  localStorage.removeItem("erc_studio_exports");
}

// Bir kombinasyonun satis fiyati: opsiyonlardan herhangi birinde sellingPrice
// (bedene sabit fiyat) varsa onu kullan; yoksa baz fiyat + priceAdjust toplami.
// Not: sellingPrice genelde Size opsiyonunda tutulur (Color'da degil), bu yuzden
// combo'nun son opsiyonu degil, ICINDEKI herhangi bir sellingPrice esas alinir.
/** Kombinasyon fiyati — sunucudaki Etsy yayiniyla AYNI kural (@erc/shared) */
export interface DuplicateGroup {
  key: string;
  listings: Array<{ id: string; title: string; sku: string | null; url: string | null; linked: boolean }>;
}

export interface DraftListItem {
  id: string;
  title: string;
  status: string;
  tags: string[];
  price: number | null;
  quantity: number;
  updatedAt: string;
  whoMade: string | null;
  whenMade: string | null;
  shippingProfileId: string | null;
  etsyListingId: string | null;
  publishNote: string | null;
  scheduledAt: string | null;
  thumbUrl: string | null;
  imageCount: number;
  descriptionLength: number;
  variationCount: number;
  shopId?: string | null; // hangi magazaya basildi (null = henuz bağlı degil)
  category?: string | null;
  /** Ortak "Etsy'ye hazir mi" kontrolu (sunucuda hesaplanir) */
  readiness?: { blockers: string[]; warnings: number };
  supplierCatalogId?: string | null;
  supplierCategoryId?: string | null;
  createdAt?: string;
  sku?: string | null;
  /** Etsy sayaclari (durum senkronunda guncellenir): satilan adet, favori, goruntulenme */
  sales?: number;
  favorites?: number;
  views?: number;
  /** Bagli tasarimlarin en yakin kopya kademesi (close/near = yakin kopya) */
  fidelity?: string | null;
  /** Kullanilan listing sablonunun adi */
  templateName?: string | null;
  /** Gorselleri ureten mockup gruplari */
  mockupTemplates?: string[];
  bigPrint?: boolean;
  videoUrl?: string | null;
}

// Bir taslagin SECILI MAGAZAYA gore durumu. Baska bir magazaya basilmis (ya da
// hicbir magazaya bagli olmayan) bir kayit, secili magazada yayinda DEGILDIR;
// bu magaza icin yayinlanmayi bekleyen bir taslaktir.
export function effectiveStatus(d: DraftListItem, activeShopId: string | null): string {
  if (d.status === "published" && d.shopId !== activeShopId) return "draft";
  return d.status;
}

// Kaydin Etsy listesi SECILI magazaya mi ait? Sadece o zaman Etsy islemleri
// (ac / aktive et / gorselleri yenile / guncelle) anlamlidir.
export function isBoundHere(d: { etsyListingId: string | null; shopId?: string | null }, activeShopId: string | null): boolean {
  return Boolean(d.etsyListingId) && d.shopId === activeShopId;
}

// Hafif liste verisinden puan (calcListingScore ile ayni kurallar)
export function calcListingScoreLite(d: DraftListItem): number {
  let score = 0;
  if (d.title.length >= 40) score += 15; else if (d.title.length >= 20) score += 8;
  if (d.tags.length >= 13) score += 15; else score += Math.round((d.tags.length / 13) * 15);
  if (d.descriptionLength >= 300) score += 15; else if (d.descriptionLength >= 100) score += 8;
  if (d.imageCount >= 5) score += 15; else score += Math.round((d.imageCount / 5) * 15);
  if (d.variationCount > 0) score += 10;
  if (d.shippingProfileId) score += 10;
  if (d.price && Number(d.price) > 0) score += 10;
  if (Number(d.quantity) > 0) score += 5;
  if (d.whoMade && d.whenMade) score += 5;
  return Math.min(100, score);
}

export function scoreColor(score: number): string {
  if (score >= 80) return "text-emerald-600 bg-emerald-50";
  if (score >= 60) return "text-blue-600 bg-blue-50";
  if (score >= 40) return "text-amber-600 bg-amber-50";
  return "text-red-600 bg-red-50";
}

export const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: "Taslak", color: "bg-gray-100 text-gray-600" },
  ready: { label: "Etsy'ye hazır", color: "bg-blue-100 text-blue-700" },
  published: { label: "Yayında", color: "bg-emerald-100 text-emerald-700" },
  scheduled: { label: "Planlı", color: "bg-purple-100 text-purple-700" },
  archived: { label: "Arşiv", color: "bg-orange-100 text-orange-700" },
};

// ======== API ========

// Aktif magaza basligi (X-Shop-Id) ortak yardimcidan gelir
